// Browser glue for device orientation: permission, events, screen angle.

import { downVectorFromEuler, rollFromOrientation, toScreenFrame, type RollReading } from './orientation';

export type MotionPermission = 'granted' | 'denied' | 'unsupported';

interface IOSOrientationEvent {
  requestPermission?: () => Promise<'granted' | 'denied'>;
}

/** Must be called from a user gesture (iOS requires it). */
export async function requestMotionPermission(): Promise<MotionPermission> {
  if (typeof DeviceOrientationEvent === 'undefined') return 'unsupported';
  const ctor = DeviceOrientationEvent as unknown as IOSOrientationEvent;
  if (typeof ctor.requestPermission === 'function') {
    try {
      return (await ctor.requestPermission()) === 'granted' ? 'granted' : 'denied';
    } catch {
      return 'denied';
    }
  }
  return 'granted';
}

export function screenAngle(): number {
  const so = globalThis.screen?.orientation;
  if (so && typeof so.angle === 'number') return so.angle;
  const legacy = (globalThis as unknown as { orientation?: number }).orientation;
  return typeof legacy === 'number' ? legacy : 0;
}

export class OrientationSensor {
  private beta = 0;
  private gamma = 0;
  private hasEvent = false;
  /** Gravity's component towards the screen's right edge (for picking a landscape). */
  get downRight(): number {
    return toScreenFrame(downVectorFromEuler(this.beta, this.gamma), screenAngle()).right;
  }
  /** Screen angle the game is drawn for; defaults to the real screen orientation. */
  angle: () => number = screenAngle;
  /** performance.now() of the last event, or 0 if none yet. */
  lastEventAt = 0;

  private readonly onEvent = (e: DeviceOrientationEvent): void => {
    if (e.beta === null || e.gamma === null) return;
    this.beta = e.beta;
    this.gamma = e.gamma;
    this.hasEvent = true;
    this.lastEventAt = performance.now();
  };

  start(): void {
    window.addEventListener('deviceorientation', this.onEvent);
  }

  stop(): void {
    window.removeEventListener('deviceorientation', this.onEvent);
  }

  /** Latest roll, computed on read so it always uses the current game angle. */
  get latest(): RollReading | undefined {
    return this.hasEvent ? rollFromOrientation(this.beta, this.gamma, this.angle()) : undefined;
  }

  get receiving(): boolean {
    return this.lastEventAt > 0 && performance.now() - this.lastEventAt < 1000;
  }
}
